import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

/**
 * De eerste kolom van een tabelrij: een klikbare titel met een potloodje om hem
 * ter plekke aan te passen.
 *
 * @param {object} props
 * @param {string} props.value - De titel zoals opgeslagen
 * @param {string} [props.to] - Route waarheen de titel linkt
 * @param {(event: object) => void} [props.onChange] - Aangeroepen bij het typen
 * @param {(event: object) => void} [props.onBlur] - Aangeroepen bij verlaten of Enter
 * @param {() => void} [props.onTitleClick] - Maakt van de titel een knop in plaats van een link
 * @param {string} [props.docid] - Belandt als `data-docid` op het invoerveld
 * @param {string} [props.placeholder] - Tekst bij een lege titel
 * @param {string} [props.className] - Extra class op de cel
 * @param {boolean} [props.readOnly] - Verbergt het potloodje
 * @param {React.ReactNode} [props.displayContent] - Vervangt de weergegeven tekst
 * @param {string} [props.list] - Id van een datalist bij het invoerveld
 * @param {React.ReactNode} [props.footer] - Extra regel onder de titel, bijvoorbeeld waarom deze rij een zoekresultaat is
 */
const TableTitleCell = ({
    value,
    to,
    onChange,
    onBlur,
    onTitleClick,
    docid,
    placeholder = '',
    className = '',
    readOnly = false,
    displayContent,
    list,
    footer,
}) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editing]);

    const startEdit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setEditing(true);
    };

    const stopEdit = () => setEditing(false);

    const handleBlur = (e) => {
        if (onBlur) onBlur(e);
        stopEdit();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (onBlur) onBlur(e);
            stopEdit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            stopEdit();
        }
    };

    const classes = ['common-table__cell', 'common-table__title-cell', className]
        .filter(Boolean)
        .join(' ');

    // Een lege titel toont de plaatshouder gedempt, zodat hij niet als echte
    // naam gelezen wordt (testronde 2026-10-28, B6: twee formulieren leken
    // allebei 'Titel van formulier' te heten).
    const displayText = value || <span className="common-table__title-placeholder">{placeholder}</span>;

    return (
        <td className={classes}>
            {editing ? (
                <input
                    ref={inputRef}
                    type="text"
                    className="common-table__title-input"
                    defaultValue={value}
                    data-docid={docid}
                    placeholder={placeholder}
                    list={list}
                    autoComplete="off"
                    onChange={onChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                />
            ) : (
                <div className="common-table__title-display">
                    {to ? (
                        <Link to={to} className="common-table__title-link">
                            {displayContent !== undefined ? displayContent : displayText}
                        </Link>
                    ) : onTitleClick ? (
                        <button
                            type="button"
                            className="common-table__title-link common-table__title-link--button"
                            onClick={onTitleClick}
                        >
                            {displayContent !== undefined ? displayContent : displayText}
                        </button>
                    ) : (
                        <span className="common-table__title-link common-table__title-link--plain">
                            {displayContent !== undefined ? displayContent : displayText}
                        </span>
                    )}
                    {readOnly ? null : (
                        <button
                            type="button"
                            className="common-table__title-edit"
                            data-testid="table-title-edit"
                            aria-label="Naam aanpassen"
                            onClick={startEdit}
                        >
                            <EditOutlinedIcon fontSize="small" />
                        </button>
                    )}
                </div>
            )}
            {footer}
        </td>
    );
};

export default TableTitleCell;
